// pages/BankAccountAddPage.tsx
import { Button } from '@/components/ui/button';
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import BankAccountCreationForm from '@/components/organisms/BankAccountCreationForm';
import { bankAccountService } from '@/services/bank-accounts.service';
import { BankAccountFormValues } from '@/services/types/bank-accounts.types';
import toast from 'react-hot-toast';
import InfoModal from '@/components/molecules/InfoModal';
import { budgetService } from '@/services/budget.service';
import { Budget } from '@/services/types/budgets.types';
import BankAccountCreatedIcon from '@/assets/icons/bank_account_created.svg';
import profileDiscardIcon from '@/assets/icons/profile-discard.svg';
import MobileAppBar from '@/components/molecules/MobileAppBar';
import { useNavigationGuard } from '@/hooks/useNavigationGuard';

const BankAccountAddPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [bankAccountId, setBankAccountId] = useState<string | null>(null);
  const [budgetDetails, setBudgetDetails] = useState<Budget | null>(null);
  const [willBeDefault, setWillBeDefault] = useState(true);

  const { budgetId } = useParams();
  const navigate = useNavigate();

  const { isDiscardModalOpen, handleCancel, handleDiscardChanges, handleDiscardModalClose, allowNavigation } =
    useNavigationGuard({
      isDirty: isFormDirty,
      fallbackPath: `/budgets/${budgetId}`
    });

  if (!budgetId) {
    console.error('Budget ID is missing from URL parameters');
    return <div>Error: Budget ID is required</div>;
  }

  useEffect(() => {
    budgetService
      .getBudget(budgetId)
      .then((response) => {
        setBudgetDetails(response.data);
        console.log('Budget Details', response);
      })
      .catch((error) => {
        console.error('Error fetching budget:', error);
      });
  }, [budgetId]);

  // A new bank account becomes the default only when no default exists yet for the budget.
  useEffect(() => {
    bankAccountService
      .getBankAccounts(budgetId)
      .then((response) => {
        const hasDefault = (response.data || []).some((ba) => ba.is_default);
        setWillBeDefault(!hasDefault);
      })
      .catch((error) => {
        console.error('Error fetching existing bank accounts:', error);
      });
  }, [budgetId]);

  const handleSubmit = async (data: BankAccountFormValues) => {
    setIsSubmitting(true);
    try {
      const bankAccountData = {
        accountName: data.accountName,
        bankName: data.bankName,
        bankCountry: data.country,
        accountNumber: data.accountNumber,
        initialAmount: typeof data.initialAmount === 'string' ? parseFloat(data.initialAmount) : data.initialAmount
      };

      const response = await bankAccountService.createBankAccount(budgetId, bankAccountData);

      if (response.data && response.data._id) {
        setBankAccountId(response.data._id);
      }

      allowNavigation();
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error creating bank account:', error);
      toast.error('Failed to create bank account. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleButtonClick = () => {
    // Trigger form submission
    const form = document.getElementById('bank-account-creation-form') as HTMLFormElement;
    if (form) {
      form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }
  };

  const handleGoToBankAccount = () => {
    setShowSuccessModal(false);
    allowNavigation();
    // Replace so this create page doesn't remain in history — otherwise the
    // next back press (mobile app bar or browser back) would return here.
    if (bankAccountId) {
      navigate(`/budgets/${budgetId}/bank-accounts/${bankAccountId}`, { replace: true });
    } else {
      // Fallback to the bank accounts list if we don't have the ID
      navigate(`/budgets/${budgetId}/bank-accounts`, { replace: true });
    }
  };

  return (
    <>
      <MobileAppBar title="Bank Account Creation" />
      <div className="bg-[#FBFBFF] p-4 flex flex-col flex-1 md:bg-white rounded-[16px] border">
        <h2 className="text-center font-bold mb-5 md:text-left md:text-[24px] md:mb-6">Create your Bank Account</h2>

        {/* FORM */}
        <BankAccountCreationForm
          onSubmit={handleSubmit}
          className="mb-6 flex-1"
          isSubmitting={isSubmitting}
          maxAmount={budgetDetails?.btev}
          isDefault={willBeDefault}
          onFormStateChange={({ isDirty }) => setIsFormDirty(isDirty)}
        />

        {/* FOOTER */}
        <footer className="py-2 bg-gradient-to-b from-[#F0EFFD]/0 from-0% to-[#F0EFFD] to-[17.74%] md:bg-white md:bg-none md:from-transparent md:to-transparent md:flex md:justify-end">
          <Button
            className="hidden md:block rounded-[24px] text-base font-semibold px-6 h-[44px] text-[16px] w-full md:w-fit text-[#E8507E]"
            variant={'text'}
            onClick={handleCancel}
            disabled={isSubmitting}
            type="button"
          >
            Cancel
          </Button>
          <Button
            className="rounded-[24px] text-base font-semibold px-6 h-[44px] text-[16px] w-full md:w-fit"
            onClick={handleButtonClick}
            disabled={isSubmitting}
            type="button"
          >
            {isSubmitting ? 'CREATING...' : 'CREATE'}
          </Button>
        </footer>
      </div>

      {isDiscardModalOpen && (
        <InfoModal
          isOpen={isDiscardModalOpen}
          variant="confirm"
          title="Are you sure you want to discard changes?"
          message="Unsaved changes will not be saved into your bank account"
          buttonText="Discard"
          onButtonClick={handleDiscardChanges}
          icon={profileDiscardIcon}
          onClose={handleDiscardModalClose}
        />
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <InfoModal
          isOpen={showSuccessModal}
          variant="success"
          title="Bank Account Created"
          message={<p>You have successfully created a Bank Account!</p>}
          buttonText="Go to Bank Account"
          onButtonClick={handleGoToBankAccount}
          icon={BankAccountCreatedIcon}
        />
      )}
    </>
  );
};

export default BankAccountAddPage;
